import { html, nothing } from 'lit';
import { ago, number } from '../../shared/ui/format.js';
import { addOwner, inviteMember, removeMember, removeOwner } from '../api/developer.js';
import { accountPath, stateVariant, word } from '../accounts.js';
import { AccountView } from './account-view.js';

/**
 * The team: who holds a seat, and who commits to its repositories without one.
 *
 * Two tables. The members: somebody who accepted an invitation, the owner who took a seat, somebody an
 * operator seated by hand - all of them hold a seat, and that is what makes them a member. An
 * invitation not yet accepted is a seat held for its address. And the authors without a seat: every
 * commit author the team's CI has seen who holds none. Their runs go through with a warning until the
 * grace period ends, and the fix is the one action on their row - invite them.
 *
 * Inviting is this page's own action, and every seat is taken is the one refusal it has: the sentence
 * is the server's, shown where the address was typed. Open invitations and the account's owners are
 * pages of their own underneath it, in the sidebar.
 */
export class UserAccountMembers extends AccountView
{
   static section = 'members';

   static properties = { inviting: { state: true }, inviteError: { state: true } };

   constructor()
   {
      super();
      this.inviting = false;
      this.inviteError = '';
   }

   get pageActions()
   {
      return [{
         id: 'invite',
         label: 'Invite somebody',
         icon: 'fa-solid fa-user-plus',
         variant: 'primary',
         disabled: this.busy
      }];
   }

   onPageAction(id)
   {
      if (id === 'invite')
      {
         this.inviting = true;
         this.inviteError = '';
      }
   }

   async invite(event)
   {
      event.preventDefault();

      const address = String(new FormData(event.target).get('email') ?? '').trim();

      if (!address)
      {
         return;
      }

      this.inviteError = '';

      const invited = await this.act(
         () => inviteMember(this.account, address),
         invite => `Invited ${invite.email}. The link was mailed to them; a seat is held for them from now, and `
            + 'accepting the link puts them in it.');

      if (invited)
      {
         this.inviting = false;
      }
      else
      {
         // The refusal belongs beside the address that was refused, not above the page.
         this.inviteError = this.notice?.message ?? '';
         this.notice = null;
      }
   }

   get body()
   {
      const usage = this.team.usage ?? {};

      // Reclaimed seats are the past, and a list somebody acts on should hold only what they can act on.
      const rows = (this.team.members ?? []).filter(member => member.state !== 'reclaimed');

      return html`
         ${usage.overQuota
            ? html`
               <lia-alert
                  variant="warning"
                  icon="fa-solid fa-triangle-exclamation"
                  heading="Over quota"
                  message=${`${number(usage.billable)} billable seat(s) against ${number(usage.seatQuota)} `
                     + 'paid for. Runs still succeed; after the grace period, reports are held back until '
                     + 'seats are bought or members removed.'}
                  .actions=${[{ id: 'billing', label: 'Buy seats', variant: 'warning', size: 'sm',
                     href: accountPath('billing', this.account) }]}
               ></lia-alert>`
            : nothing}

         ${this.inviting ? this.inviteForm : nothing}

         <lia-card>
            <lia-data-table
               listing-id="account-members"
               .perPage=${10}
               no-column-manager
               ?loading=${this.busy}
               .columns=${[
                  {
                     key: 'email',
                     label: 'Developer',
                     sortable: true,
                     searchable: true,
                     render: row => html`
                        <div class="text-truncate">
                           ${row.displayName && row.displayName !== row.email
                              ? html`<span>${row.displayName}</span>
                                 <span class="text-body-secondary small ms-1">${row.email}</span>`
                              : row.email}
                           ${row.you ? html`<span class="badge text-bg-secondary ms-1">You</span>` : nothing}
                        </div>`
                  },
                  {
                     key: 'state',
                     label: 'Seat',
                     sortable: true,
                     render: row => html`
                        <lia-badge variant=${stateVariant(row.state)}>
                           ${row.state === 'pending' ? 'Invited' : word(row.state)}
                        </lia-badge>`
                  },
                  {
                     key: 'owner',
                     label: 'Role',
                     sortable: true,
                     render: row => (row.owner
                        ? html`<lia-badge variant="primary">Owner</lia-badge>`
                        : html`<span class="text-body-secondary">Member</span>`)
                  },
                  {
                     key: 'lastSeenAt',
                     label: 'Last run',
                     sortable: true,

                     // An invitation stamps the seat when it is sent, and nobody has run anything yet.
                     render: row => (row.state === 'pending' && row.assignmentMode === 'invited'
                        ? 'Not yet'
                        : row.lastSeenAt ? ago(row.lastSeenAt) : 'Never')
                  },
                  { key: 'assignmentMode', label: 'How', sortable: true, render: row => howWord(row.assignmentMode) }
               ]}
               .rows=${rows}
               .rowKey=${row => row.id}
               empty-title="Nobody yet"
               empty-message="Nobody holds a seat here yet."
               .emptyActions=${[{ id: 'invite', label: 'Invite somebody', icon: 'fa-solid fa-user-plus', variant: 'primary' }]}
               .rowActions=${row => this.memberActions(row)}
               @lia-action=${event => this.onMemberAction(event.detail?.action, event.detail?.row)}
            ></lia-data-table>
         </lia-card>

         ${this.unseated}`;
   }

   get inviteForm()
   {
      return html`
         <lia-card title="Invite somebody" icon="fa-solid fa-user-plus">
            ${this.inviteError
               ? html`<lia-alert variant="danger" icon="fa-solid fa-triangle-exclamation" message=${this.inviteError}></lia-alert>`
               : nothing}
            <form class="d-flex flex-wrap gap-2" @submit=${this.invite}>
               <input
                  class="form-control form-control-sm flex-grow-1 covdbg-address"
                  type="email"
                  name="email"
                  placeholder="colleague@example.com"
                  aria-label="Address to invite"
                  required
                  autofocus
                  ?disabled=${this.busy}
               >
               <button class="btn btn-sm btn-primary" type="submit" ?disabled=${this.busy}>Send invitation</button>
               <button
                  class="btn btn-sm btn-link"
                  type="button"
                  @click=${() => { this.inviting = false; this.inviteError = ''; }}
               >Cancel</button>
            </form>
         </lia-card>`;
   }

   /** The commit authors the team's CI has seen without a seat, each with the invitation as the fix. */
   get unseated()
   {
      const rows = this.team.unseatedAuthors ?? [];

      return html`
         <lia-card title="Authors without a seat" icon="fa-solid fa-user-clock">
            <p class="small text-body-secondary">
               Commit authors your CI has run covdbg for who hold no seat here. Their runs go through
               with a warning; after ten days their reports are held back until they are invited.
            </p>
            <lia-data-table
               listing-id="account-unseated"
               .perPage=${10}
               no-column-manager
               ?loading=${this.busy}
               .columns=${[
                  { key: 'email', label: 'Address', sortable: true, searchable: true },
                  { key: 'remoteKey', label: 'Last repository', sortable: true, searchable: true, render: row => row.remoteKey || '—' },
                  { key: 'runs', label: 'Decisions', sortable: true, render: row => number(row.runs ?? 0) },
                  { key: 'firstSeenAt', label: 'First seen', sortable: true, render: row => (row.firstSeenAt ? ago(row.firstSeenAt) : '—') },
                  { key: 'lastSeenAt', label: 'Last seen', sortable: true, render: row => (row.lastSeenAt ? ago(row.lastSeenAt) : '—') }
               ]}
               .rows=${rows}
               .rowKey=${row => `${row.email}|${row.remoteKey}`}
               empty-title="Nobody without a seat"
               empty-message="Everybody your CI has run covdbg for holds a seat here."
               .rowActions=${() => [{
                  id: 'invite-author',
                  icon: 'fa-solid fa-envelope',
                  title: 'Invite this address',
                  variant: 'outline-primary',
                  size: 'sm',
                  disabled: this.busy
               }]}
               @lia-action=${event =>
               {
                  const row = event.detail?.row;

                  if (row)
                  {
                     void this.act(
                        () => inviteMember(this.account, row.email),
                        invite => `Invited ${invite.email}. The link was mailed to them, and a seat is held for them from now.`);
                  }
               }}
            ></lia-data-table>
         </lia-card>`;
   }

   memberActions(row)
   {
      const actions = [];

      if (row.principalId && !row.you)
      {
         actions.push(row.owner
            ? {
               id: 'demote',
               icon: 'fa-solid fa-user-minus',
               title: 'Remove as owner',
               variant: 'outline-secondary',
               size: 'sm',
               disabled: this.busy,
               confirm: {
                  title: 'Remove as owner?',
                  message: `${row.email} keeps their seat and stops being able to manage this team.`,
                  confirmLabel: 'Remove as owner'
               }
            }
            : {
               id: 'promote',
               icon: 'fa-solid fa-user-shield',
               title: 'Make an owner',
               variant: 'outline-secondary',
               size: 'sm',
               disabled: this.busy,
               confirm: {
                  title: 'Make an owner?',
                  message: `${row.email} will be able to invite and remove members, issue tokens and buy `
                     + 'seats - everything you can do here.',
                  confirmLabel: 'Make an owner'
               }
            });
      }

      actions.push({
         id: 'remove',
         icon: 'fa-solid fa-xmark',
         title: row.you ? 'Give up your seat' : 'Remove from the team',
         variant: 'outline-danger',
         size: 'sm',
         disabled: this.busy,
         confirm: {
            title: row.you ? 'Give up your seat?' : `Remove ${row.email}?`,
            message: 'The seat stops counting now and cannot be handed to somebody else for seven days. '
               + (row.you
                  ? 'You stay an owner; only the seat goes.'
                  : 'Their runs in CI are listed below as an author without a seat until they are invited again.'),
            confirmLabel: 'Remove',
            variant: 'danger'
         }
      });

      return actions;
   }

   onMemberAction(action, row)
   {
      if (action?.id === 'invite')
      {
         this.inviting = true;
         this.inviteError = '';
         return;
      }

      if (!action || !row)
      {
         return;
      }

      switch (action.id)
      {
      case 'promote':
         void this.act(() => addOwner(this.account, row.email), `${row.email} now owns this team too.`);
         break;
      case 'demote':
         void this.act(() => removeOwner(this.account, row.principalId), `${row.email} is no longer an owner.`);
         break;
      case 'remove':
         void this.act(() => removeMember(this.account, row.id), `Removed ${row.email}.`);
         break;
      default:
         break;
      }
   }
}

/** How a seat came to exist, to the person paying for it. */
function howWord(mode)
{
   switch (mode)
   {
   case 'invited':
      return 'Invited';
   case 'manual':
      return 'Taken';
   default:
      return word(mode);
   }
}

customElements.define('user-account-members', UserAccountMembers);
